import { ComponentProps, PropsWithChildren, ReactNode } from "react";

import Tooltip from "@/components/elements/Tooltip";
import { Step, useStepsContext } from "@/components/modules/stepper";
import cn from "@/helpers/classNames";
import usePrevious from "@/hooks/usePrevious";
import { Tab, Transition } from "@headlessui/react";

export function StepGroup(props: ComponentProps<typeof Tab.Group>) {
  return <Tab.Group {...props}>{props.children}</Tab.Group>;
}

export function StepList(props: {
  index: number;
  length: number;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Tab.List className={cn("w-full px-6 pt-4 text-sm", props.className)}>
      <div className="flex w-full justify-between">{props.children}</div>
      <div className="h-1.5 w-full rounded-full bg-foreground/10 shadow-sm">
        <div
          className="h-full rounded-full bg-foreground/80 transition-all duration-300 ease-in-out"
          style={{ width: `${((props.index + 1) / props.length) * 100}%` }}
        />
      </div>
    </Tab.List>
  );
}

export function StepItem(
  props: PropsWithChildren<{
    index: number;
    step: Step;
    isDisabled?: boolean;
    isValid?: boolean;
  }>,
) {
  const { step } = props;
  const isDisabled = step.isDisabled || props.isDisabled;
  return (
    <Tab
      key={step.id}
      className={({ selected }) =>
        cn(
          "relative w-full min-w-0 flex-1 rounded-sm py-1",
          isDisabled && "cursor-auto text-gray-400",
          !isDisabled && selected && "font-medium",
          !isDisabled && "hover:bg-foreground/5",
          step.isHidden && "hidden",
        )
      }
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) {
          e.preventDefault();
        }
      }}
    >
      {({ selected }) => (
        <Tooltip content={step.tooltip ?? null}>
          <div className="truncate px-1 text-center">
            <span
              className={cn(
                "block origin-bottom truncate whitespace-nowrap text-sm transition-transform",
                {
                  "scale-105": selected,
                },
              )}
            >
              {props.index + 1}. {step.title}
            </span>
          </div>
        </Tooltip>
      )}
    </Tab>
  );
}

export function StepPanels(props: { children: ReactNode }) {
  return (
    <Tab.Panels className="h-full grow">
      <div className="relative h-full">{props.children}</div>
    </Tab.Panels>
  );
}

export function StepPanel(props: {
  show?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const stepContext = useStepsContext();

  const { currentStepIndex } = stepContext;
  const previousStepIndex = usePrevious(currentStepIndex);

  const pagingDirection =
    previousStepIndex === undefined || previousStepIndex <= currentStepIndex
      ? "FORWARD"
      : "BACKWARD";

  return (
    <Transition
      show={props.show}
      enter="transition duration-500 ease-in-out"
      enterFrom={cn("opacity-0", {
        "translate-x-full": pagingDirection === "FORWARD",
        "-translate-x-full": pagingDirection === "BACKWARD",
      })}
      enterTo="translate-x-0 opacity-100"
      leave="transition duration-500 ease-in-out"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo={cn("opacity-0", {
        "-translate-x-full": pagingDirection === "FORWARD",
        "translate-x-full": pagingDirection === "BACKWARD",
      })}
      className={cn("absolute h-full w-full bg-white dark:bg-gray-800")}
    >
      <Tab.Panel
        className={cn(
          "flex h-full w-full flex-col items-center focus:outline-none",
          props.className,
        )}
        static
      >
        {props.children}
      </Tab.Panel>
    </Transition>
  );
}
